/**
 * AppointmentManagementPage Component
 *
 * Appointment management page showing:
 * - Search functionality
 * - Create appointment button
 * - Appointments table with patient info, status, and actions
 * - Pagination controls
 */

import { CAPABILITIES } from '@/lib/types/authorization';
import { useCan } from '@/lib/hooks/useCan';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  FileText,
  Mic,
  Pencil,
  Trash2,
} from 'lucide-react';
import { RefreshButton } from '@/components/ui/refresh-button';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button, Modal } from '@/components/base';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/base/Form/Select';
import appointmentsApi, { AppointmentListItem, AppointmentStatus } from '@/lib/api/appointments';
import { medplumResourcesApi } from '@/lib/api/medplum-resources';
import {
  ScheduleAppointmentModal,
  type PatientData,
} from '@/components/modals/ScheduleAppointmentModal';
import { EditAppointmentModal } from '@/components/modals/EditAppointmentModal';
import { PatientSearchModal } from '@/components/modals/PatientSearchModal';
import { PatientFormModal } from '@/components/modals/PatientFormModal';
import { PatientFormContent } from '@/components/forms/PatientFormContent';
import { useAuthStore } from '@/lib/stores/authStore';
import { createComponentLogger } from '@/lib/utils/logger';
import { getConsultationPath, startConsultationForPatient } from '@/lib/utils/encounter';
import { draftsApi } from '@/lib/api/drafts';
import {
  calculateAge,
  formatGenderDisplay,
  getPhoneFromTelecom,
  getPatientNameFromFhir,
} from '@/lib/types/patient';
import { getAvatarColors } from '@/lib/utils/avatar';
import {
  formatAppointmentDateTime,
  getStatusDisplay,
  isConsultationDisabled,
  isDeleteLocked,
  APPOINTMENTS_TABLE_GRID,
  APPOINTMENTS_TABLE_GRID_WITH_DOCTOR,
  AppointmentRowData,
} from '@/components/shared/AppointmentsTable';

const logger = createComponentLogger('AppointmentManagementPage');

interface AppointmentData {
  id: string;
  patientName: string;
  patientId: string;
  registrationNumber: string;
  age: number;
  gender: 'Male' | 'Female' | 'Other';
  contact: string;
  visitType: string;
  status: string;
  originalStatus: AppointmentStatus; // For API calls
  appointmentStart?: string; // ISO date string
  appointmentEnd?: string; // ISO date string
  encounterId?: string; // Encounter ID for consultation
  doctorName?: string; // Doctor name for administrator view
  practitionerId?: string; // Practitioner ID for editing
  imageUrl?: string;
}

// Items per page
const ITEMS_PER_PAGE = 10;

export function AppointmentManagementPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const projectConfig = useAuthStore((state) => state.projectConfig);

  // Doctor filtering is available to administrators when enabled for the project.
  const canConsult = useCan(CAPABILITIES.CONSULTATION_START);
  const showDoctorUi = user?.role === 'admin' && projectConfig?.services.showDoctorUi !== false;
  const showDoctorColumn = showDoctorUi;

  // URL-based modal state for Add Patient
  const addPatientFromQuery = searchParams.get('addPatient');
  const prefillPhoneFromQuery = searchParams.get('prefillPhone');

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Doctor filter state
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | undefined>(undefined);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentRowData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Create appointment modal states (using PatientSearchModal component)
  const [patientSearchModalOpen, setPatientSearchModalOpen] = useState(false);
  const [scheduleModalPatient, setScheduleModalPatient] = useState<PatientData | null>(null);
  const [isStartingConsultationFromSearch, setIsStartingConsultationFromSearch] = useState(false); // Track if starting consultation from search modal

  const appointmentsHeaderClassName = 'font-hanken font-normal tracking-[-0.018px] text-[#061A61]';

  const getStatusPillClassName = (status: string, originalStatus?: string) => {
    const statusKey = (originalStatus || status || '').toLowerCase();
    const baseClassName =
      'inline-flex min-w-[60px] items-center justify-center gap-[4px] rounded-full border px-2 py-0.5 font-hanken font-medium';

    if (statusKey === 'arrived') {
      return `${baseClassName} bg-[#E8F7EF] border-[#B6E6CC] text-[#1E9E62]`;
    }

    if (statusKey === 'booked') {
      return `${baseClassName} bg-[#EEF3FF] border-[#D1DBFD] text-[#6B8AF9]`;
    }

    return `${baseClassName} bg-[#f8f9fb] border-[#eaebf3] text-[#555661]`;
  };

  // Debounce search query with longer delay for server-side search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 500); // Longer debounce for server-side search

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset page when doctor filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDoctorId]);

  // Fetch practitioners list for the administrator doctor filter
  const { data: practitionersData } = useQuery({
    queryKey: ['practitioners-for-filter'],
    queryFn: async () => {
      return medplumResourcesApi.listPractitioners({ count: 100 });
    },
    enabled: showDoctorUi,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Server-side search + pagination
  // When search query is provided, use FHIR chained search (patient.name)
  // Administrators see all appointments with an optional doctor filter.
  const {
    data: appointmentsData,
    isLoading: isLoadingAppointments,
    error: appointmentsError,
  } = useQuery({
    queryKey: [
      'appointments',
      'supervisor',
      currentPage,
      debouncedQuery,
      showDoctorUi ? selectedDoctorId : null,
    ],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User ID not available');
      }

      const offset = (currentPage - 1) * ITEMS_PER_PAGE;

      const searchParams: Parameters<typeof appointmentsApi.listAppointments>[0] = {
        count: ITEMS_PER_PAGE,
        offset: offset,
        sort: '-date',
        total: 'accurate',
        include: ['Appointment:patient', 'Appointment:actor'],
      };

      // Apply doctor filter
      if (showDoctorUi && selectedDoctorId) {
        searchParams.actor = `Practitioner/${selectedDoctorId}`;
      }
      // Access policy handles what appointments the user can see — no explicit actor constraint needed

      // Add server-side search by patient name if query exists
      if (debouncedQuery.trim()) {
        // Check if it looks like a phone number (digits only or with common phone chars)
        const isPhoneNumber = /^[\d\s\-+()]+$/.test(debouncedQuery.trim());

        if (isPhoneNumber) {
          searchParams.patientPhone = debouncedQuery.trim();
          logger.log(`Server-side search by phone: ${debouncedQuery}`);
        } else {
          searchParams.patientName = debouncedQuery.trim();
          logger.log(`Server-side search by name: ${debouncedQuery}`);
        }
      } else {
        logger.log(`Fetching appointments page ${currentPage}`);
      }

      return appointmentsApi.listAppointments(searchParams);
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // Cache for 30 seconds
    placeholderData: (previousData) => previousData, // Keep previous data while loading
  });

  // Use included patients and practitioners from the appointment response
  const patientsMap = appointmentsData?.patients;
  const practitionersMap = appointmentsData?.practitioners;

  const isLoading = isLoadingAppointments;
  const error = appointmentsError;

  // Transform appointments with patient data from included resources
  const displayedAppointments: AppointmentData[] = useMemo(() => {
    if (!appointmentsData?.appointments) return [];

    return appointmentsData.appointments.map((apt: AppointmentListItem) => {
      // Show the status directly from API with proper capitalization
      const displayStatus = apt.status
        ? apt.status.charAt(0).toUpperCase() + apt.status.slice(1).toLowerCase()
        : 'Unknown';

      // Patient ID - apt.patientId is already just the ID (not full reference)
      const patientId = apt.patientId || '';
      // For display, show a short version or full ID
      const registrationId = patientId ? `PR${patientId.slice(0, 6).toUpperCase()}` : 'N/A';

      // Get patient data from the fetched patients map
      const patient = patientId && patientsMap ? patientsMap.get(patientId) : undefined;

      // Extract patient details dynamically from FHIR Patient resource
      const patientName = patient
        ? getPatientNameFromFhir(patient.name)
        : apt.patientName || 'Unknown Patient';
      const age = patient?.birthDate ? calculateAge(patient.birthDate) : null;
      const gender = patient ? formatGenderDisplay(patient.gender) : ('Other' as const);
      const contact = patient ? getPhoneFromTelecom(patient.telecom) : 'N/A';

      // Get practitioner name from included resources if available, fallback to API response
      let doctorName = apt.practitionerName;
      if (apt.practitionerId && practitionersMap) {
        const practitioner = practitionersMap.get(apt.practitionerId);
        if (practitioner?.name?.[0]) {
          const name = practitioner.name[0];
          doctorName =
            `${name.given?.join(' ') || ''} ${name.family || ''}`.trim() || apt.practitionerName;
        }
      }

      return {
        id: apt.id,
        patientName,
        patientId: patientId, // Just the ID
        registrationNumber: registrationId,
        age: age ?? 0,
        gender,
        contact,
        visitType: apt.serviceType || apt.appointmentType || 'Consultation',
        status: displayStatus,
        originalStatus: (apt.status || 'booked') as AppointmentStatus,
        appointmentStart: apt.start,
        appointmentEnd: apt.end,
        encounterId: apt.encounterId,
        doctorName, // Include doctor name for administrator view
        practitionerId: apt.practitionerId, // Include practitioner ID for editing
        imageUrl: undefined,
      };
    });
  }, [appointmentsData, patientsMap, practitionersMap]);

  // Server-side pagination - use total from API response
  const totalItems = appointmentsData?.total ?? 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const hasNextPage = appointmentsData?.hasNext ?? false;
  const hasPrevPage = appointmentsData?.hasPrev ?? false;

  // Handlers
  const handleCreateAppointment = () => {
    setPatientSearchModalOpen(true);
  };

  const handlePatientSelected = (patient: PatientData) => {
    setScheduleModalPatient(patient);
  };

  const handleAppointmentCreated = () => {
    // Refetch appointments list (invalidate both page queries to ensure sync)
    queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
    queryClient.invalidateQueries({
      queryKey: ['dashboard-practitioner-appointments'],
      exact: false,
    });
    setCurrentPage(1); // Reset to first page after creating
  };

  // Close patient form modal handler
  const handleCloseFormModal = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('addPatient');
    newParams.delete('prefillPhone');
    setSearchParams(newParams);
  };

  // Handle form submission success
  const handleFormSuccess = () => {
    handleCloseFormModal();
    // Refetch appointments (invalidate both page queries to ensure sync)
    queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
    queryClient.invalidateQueries({
      queryKey: ['dashboard-practitioner-appointments'],
      exact: false,
    });
  };

  // Handle switching from Add Patient form to Schedule Appointment
  const handleSwitchToAppointment = (patient: {
    id: string;
    name: string;
    phoneNumber: string;
    age?: number;
    gender: 'M' | 'F' | 'O' | 'U';
  }) => {
    handleCloseFormModal();
    setScheduleModalPatient({
      uuid: patient.id,
      name: patient.name,
      age: patient.age ?? 0,
      gender: patient.gender,
      phoneNumber: patient.phoneNumber,
    });
  };

  // Handle switching from Patient Search to Add Patient form
  const handleSwitchToCreatePatient = (phoneNumber: string) => {
    setPatientSearchModalOpen(false);
    setSearchParams({ addPatient: 'true', prefillPhone: phoneNumber });
  };

  // Handle Start Consultation directly from Patient Search Modal
  const handleStartConsultationFromSearch = async (patient: PatientData) => {
    if (!user?.id) {
      toast.error('User not authenticated');
      return;
    }

    // Prevent multiple simultaneous requests
    if (isStartingConsultationFromSearch) return;

    setIsStartingConsultationFromSearch(true);
    toast.info('Preparing consultation...', { duration: 2000 });

    try {
      const { encounterId } = await startConsultationForPatient(patient.uuid);

      // Keep appointment lists in sync across pages
      queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
      queryClient.invalidateQueries({
        queryKey: ['dashboard-practitioner-appointments'],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: ['dashboard-patients'],
        exact: false,
      });

      toast.success('Ready for consultation');
      setPatientSearchModalOpen(false);
      navigate(`/consultation/${encounterId}`);
    } catch (error) {
      logger.error('Failed to start consultation from search:', error);
      toast.error('Failed to start consultation. Please try again.');
      // Modal stays open on error so user can retry
    } finally {
      setIsStartingConsultationFromSearch(false);
    }
  };

  const handleViewDetails = (apt: AppointmentData) => {
    // Navigate to patient record page
    if (apt.patientId) {
      navigate(`/patient-record/${apt.patientId}`);
    }
  };

  // State for creating encounter
  const [isCreatingEncounter, setIsCreatingEncounter] = useState<string | null>(null);

  const handleStartConsultation = async (apt: AppointmentData) => {
    if (isCreatingEncounter) return; // Prevent double-clicking

    // If encounter already exists, navigate directly
    if (apt.encounterId) {
      const path = getConsultationPath(apt.encounterId);
      if (path) {
        // For appointments that already have an encounter (e.g. on-hold/resumed),
        // fetch draft data and pass continuation state like DashboardPage does
        try {
          const draft = await draftsApi.getDraft(apt.encounterId);
          const previousSummary = draft?.cdssData ? JSON.stringify(draft.cdssData) : '{}';
          toast.info('Opening consultation...', { duration: 2000 });
          navigate(path, {
            state: { isContinuation: true, previousSummary },
          });
        } catch {
          toast.info('Opening consultation...', { duration: 2000 });
          navigate(path);
        }
        return;
      }
    }

    // No encounter exists - create one first
    if (!apt.patientId) {
      toast.error('Cannot start consultation: Patient information not found');
      return;
    }

    if (!user?.id) {
      toast.error('Cannot start consultation: User not authenticated');
      return;
    }

    toast.info('Preparing consultation...', { duration: 2000 });
    setIsCreatingEncounter(apt.id);

    try {
      const { encounterId } = await startConsultationForPatient(apt.patientId);

      toast.success('Ready for consultation');
      navigate(`/consultation/${encounterId}`);
    } catch (error) {
      logger.error('Failed to start consultation:', error);
      toast.error('Failed to start consultation. Please try again.');
    } finally {
      setIsCreatingEncounter(null);
    }
  };

  // Helper to convert AppointmentData to AppointmentRowData format for shared modals
  const toAppointmentRowData = (apt: AppointmentData): AppointmentRowData => ({
    id: apt.id,
    patientName: apt.patientName,
    patientId: apt.patientId,
    age: apt.age,
    gender: apt.gender,
    contact: apt.contact,
    visitType: apt.visitType,
    status: apt.status,
    originalStatus: apt.originalStatus,
    appointmentStart: apt.appointmentStart,
    appointmentEnd: apt.appointmentEnd,
    encounterId: apt.encounterId,
    doctorName: apt.doctorName,
    practitionerId: apt.practitionerId,
    imageUrl: apt.imageUrl,
  });

  const handleEdit = (apt: AppointmentData) => {
    setSelectedAppointment(toAppointmentRowData(apt));
    setEditModalOpen(true);
  };

  const handleDelete = (apt: AppointmentData) => {
    setSelectedAppointment(toAppointmentRowData(apt));
    setDeleteModalOpen(true);
  };

  const handleAppointmentUpdated = () => {
    // Invalidate and refetch appointments
    queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
    queryClient.invalidateQueries({
      queryKey: ['dashboard-practitioner-appointments'],
      exact: false,
    });
    setEditModalOpen(false);
    setSelectedAppointment(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAppointment) return;

    // Defense in depth: the delete button is already disabled in the UI for
    // this case, but guard here too so nothing can delete an appointment
    // whose consultation is started and on hold ("checked-in").
    if (isDeleteLocked(selectedAppointment.originalStatus, selectedAppointment.encounterId)) {
      toast.error('Cannot delete — consultation is on hold. Finish or cancel it first.');
      setDeleteModalOpen(false);
      setSelectedAppointment(null);
      return;
    }

    setIsDeleting(true);
    try {
      await appointmentsApi.deleteAppointment(selectedAppointment.id);

      // Invalidate and refetch appointments (both page queries to ensure sync)
      queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false });
      queryClient.invalidateQueries({
        queryKey: ['dashboard-practitioner-appointments'],
        exact: false,
      });
      setCurrentPage(1); // Reset to first page after deleting

      setDeleteModalOpen(false);
      setSelectedAppointment(null);
      logger.log('Appointment deleted successfully:', selectedAppointment.id);
    } catch (error) {
      logger.error('Failed to delete appointment:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  return (
    <AppLayout>
      <div className="relative flex w-full flex-col bg-[#e1e3ed]">
        <div className="flex flex-col py-6">
          {/* Header Section - Title, Search, Button */}
          <div className="flex items-center justify-between gap-4">
            {/* Title */}
            <h1 className="flex-shrink-0 font-hanken text-[calc(20px*var(--app-scale))] font-medium tracking-normal text-gray-700">
              Appointment Management
            </h1>

            {/* Search and Button - Right aligned */}
            <div className="flex items-center gap-3">
              {/* Search Bar */}
              <div className="relative w-[280px]">
                <Search className="absolute left-3 top-1/2 h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] -translate-y-1/2 text-gray-700" />
                <input
                  type="text"
                  placeholder="Search Patient"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-full bg-white py-2 pl-10 pr-4 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-gray-700 shadow-sm placeholder:text-[#bcc0d7] focus:outline-none focus:ring-2 focus:ring-[#061a61]/20"
                />
              </div>

              {/* Doctor Filter */}
              {showDoctorUi && (
                <Select value={selectedDoctorId} onValueChange={setSelectedDoctorId}>
                  <SelectTrigger className="h-9 w-[200px] rounded-lg border-gray-200 bg-white font-hanken text-[calc(16px*var(--app-scale))] text-gray-700">
                    <SelectValue placeholder="All Doctors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Doctors</SelectItem>
                    {practitionersData?.practitioners.map((practitioner: any) => {
                      const name = practitioner.name?.[0];
                      const displayName = name
                        ? `${name.prefix?.[0] || ''} ${name.given?.join(' ') || ''} ${name.family || ''}`.trim()
                        : practitioner.id || 'Unknown';
                      return (
                        <SelectItem key={practitioner.id} value={practitioner.id || ''}>
                          {displayName}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}

              {/* Create Appointment Button */}
              <Button
                onClick={handleCreateAppointment}
                size="sm"
                className="flex h-9 items-center gap-1 rounded-lg bg-[#061a61] px-3 font-hanken text-[calc(16px*var(--app-scale))] font-medium text-white hover:bg-[#0a2480]"
              >
                <Plus className="h-[calc(16px*var(--app-scale))] w-[calc(16px*var(--app-scale))]" />
                Create Appointment
              </Button>
            </div>
          </div>

          {/* Table Section */}
          <div className="mt-4 flex flex-col">
            <div className="relative flex flex-col overflow-x-auto rounded-3xl bg-white shadow-sm">
              {/* Refresh Button - positioned in top right corner of table */}
              <RefreshButton
                onClick={() =>
                  queryClient.invalidateQueries({ queryKey: ['appointments'], exact: false })
                }
                isLoading={isLoading}
                className="absolute right-4 top-3"
              />
              <div
                className={`flex flex-col ${showDoctorColumn ? 'min-w-[1120px]' : 'min-w-[960px]'}`}
              >
                {/* Table Header with inline filters */}
                <div
                  className={`grid flex-shrink-0 ${showDoctorColumn ? APPOINTMENTS_TABLE_GRID_WITH_DOCTOR : APPOINTMENTS_TABLE_GRID} items-center gap-[2px] border-b border-gray-200 px-4 py-3`}
                >
                  <div className="text-left">
                    <p
                      className={`${appointmentsHeaderClassName} text-[calc(20px*var(--app-scale))] leading-[calc(24px*var(--app-scale))]`}
                    >
                      Patient name
                    </p>
                  </div>
                  <div className="text-center">
                    <p
                      className={`${appointmentsHeaderClassName} text-[calc(20px*var(--app-scale))] leading-[calc(24px*var(--app-scale))]`}
                    >
                      Age/Gender
                    </p>
                  </div>
                  <div className="text-center">
                    <p
                      className={`${appointmentsHeaderClassName} text-[calc(20px*var(--app-scale))] leading-[calc(24px*var(--app-scale))]`}
                    >
                      Contact
                    </p>
                  </div>
                  {showDoctorUi && (
                    <div className="text-center">
                      <p
                        className={`${appointmentsHeaderClassName} text-[calc(20px*var(--app-scale))] leading-[calc(24px*var(--app-scale))]`}
                      >
                        Doctor
                      </p>
                    </div>
                  )}
                  <div className="text-center">
                    <p
                      className={`${appointmentsHeaderClassName} text-[calc(20px*var(--app-scale))] leading-[calc(24px*var(--app-scale))]`}
                    >
                      Visit type
                    </p>
                  </div>
                  <div className="text-center">
                    <p
                      className={`${appointmentsHeaderClassName} text-[calc(20px*var(--app-scale))] leading-[calc(24px*var(--app-scale))]`}
                    >
                      Appointment Time
                    </p>
                  </div>
                  <div className="text-center">
                    <p
                      className={`${appointmentsHeaderClassName} text-[calc(20px*var(--app-scale))] leading-[calc(24px*var(--app-scale))]`}
                    >
                      Status
                    </p>
                  </div>
                  <div className="text-center">
                    <p
                      className={`${appointmentsHeaderClassName} text-[calc(20px*var(--app-scale))] leading-[calc(24px*var(--app-scale))]`}
                    >
                      Action
                    </p>
                  </div>
                </div>

                {/* Table Body */}
                {isLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="h-[calc(32px*var(--app-scale))] w-[calc(32px*var(--app-scale))] animate-spin rounded-full border-4 border-[#6b8af9] border-t-transparent" />
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-20">
                    <p className="font-hanken text-[calc(16px*var(--app-scale))] text-red-500">
                      Failed to load appointments. Please try again.
                    </p>
                  </div>
                ) : displayedAppointments.length === 0 ? (
                  <div className="flex items-center justify-center py-20">
                    <p className="font-hanken text-[calc(16px*var(--app-scale))] text-gray-700">
                      {debouncedQuery
                        ? 'No appointments found matching your search.'
                        : 'No appointments found.'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {displayedAppointments.map((apt) => (
                      <div
                        key={apt.id}
                        className={`grid ${showDoctorColumn ? APPOINTMENTS_TABLE_GRID_WITH_DOCTOR : APPOINTMENTS_TABLE_GRID} items-center gap-[2px] px-4 py-2 hover:bg-gray-50`}
                      >
                        {/* Patient Info */}
                        <div className="flex items-center gap-3">
                          {(() => {
                            const colors = getAvatarColors(apt.patientName);
                            return (
                              <div
                                className="flex h-[calc(50px*var(--app-scale))] w-[calc(50px*var(--app-scale))] flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/50"
                                style={{ backgroundColor: colors.bg }}
                              >
                                {apt.imageUrl ? (
                                  <img
                                    src={apt.imageUrl}
                                    alt={apt.patientName}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <span
                                    className="font-hanken text-[calc(24px*var(--app-scale))] font-medium leading-[calc(28px*var(--app-scale))]"
                                    style={{ color: colors.text }}
                                  >
                                    {apt.patientName.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <p className="truncate font-hanken text-[calc(20px*var(--app-scale))] font-medium leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                              {apt.patientName}
                            </p>
                          </div>
                        </div>

                        {/* Age/Gender */}
                        <div className="text-center">
                          <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                            {apt.age > 0 ? apt.age : '-'} {apt.gender}
                          </p>
                        </div>

                        {/* Contact */}
                        <div className="text-center">
                          <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                            {apt.contact}
                          </p>
                        </div>

                        {/* Doctor Name - Visible when showDoctorUi is true */}
                        {showDoctorUi && (
                          <div className="text-center">
                            <p className="truncate font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                              {apt.doctorName || 'N/A'}
                            </p>
                          </div>
                        )}

                        {/* Visit Type */}
                        <div className="text-center">
                          <p className="truncate font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                            {apt.visitType}
                          </p>
                        </div>

                        {/* Date & Time */}
                        <div className="text-center">
                          <p className="font-hanken text-[calc(20px*var(--app-scale))] font-normal leading-[calc(24px*var(--app-scale))] tracking-[-0.18px] text-[#555661]">
                            {formatAppointmentDateTime(apt.appointmentStart)}
                          </p>
                        </div>

                        {/* Status */}
                        <div className="text-center">
                          <span
                            className={`${getStatusPillClassName(apt.status, apt.originalStatus)} text-[calc(14px*var(--app-scale))]`}
                          >
                            {getStatusDisplay(apt.status, apt.originalStatus)}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-shrink-0 items-center justify-end gap-2">
                          {/* View Details  */}
                          <button
                            onClick={() => handleViewDetails(apt)}
                            className="flex flex-shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-[#eaebf3] bg-[#eef6ff] px-2 hover:bg-[#dbeafe]"
                            style={{ height: 'calc(36px * var(--app-scale))' }}
                          >
                            <FileText className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] flex-shrink-0 text-[#566ec7]" />
                            <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium leading-[calc(20px*var(--app-scale))] tracking-[-0.18px] text-[#6b8af9]">
                              View details
                            </span>
                          </button>

                          {/* Mic Button - Start Consultation (only for roles with the capability) */}
                          {canConsult &&
                            (() => {
                              const disabled = isConsultationDisabled(apt.originalStatus);
                              return (
                                <button
                                  onClick={() => handleStartConsultation(apt)}
                                  disabled={disabled}
                                  className={`flex h-[calc(36px*var(--app-scale))] w-[calc(36px*var(--app-scale))] items-center justify-center rounded-lg border border-[#eaebf3] ${
                                    disabled
                                      ? 'cursor-not-allowed bg-gray-100 opacity-50'
                                      : 'bg-[#eef6ff] hover:bg-[#dbeafe]'
                                  }`}
                                  title={
                                    disabled ? 'Consultation not available' : 'Start Consultation'
                                  }
                                >
                                  <Mic
                                    className={`${disabled ? 'text-gray-400' : 'text-[#6b8af9]'} h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))]`}
                                  />
                                </button>
                              );
                            })()}

                          {/* Edit Appointment Button */}
                          <button
                            onClick={() => handleEdit(apt)}
                            className="flex h-[calc(36px*var(--app-scale))] w-[calc(36px*var(--app-scale))] items-center justify-center rounded-lg border border-[#eaebf3] bg-[#eef6ff] hover:bg-[#dbeafe]"
                            title="Edit Appointment"
                          >
                            <Pencil className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#6b8af9]" />
                          </button>

                          {/* Delete Button — disabled once a consultation has
                              been started and put on hold ("checked-in"), so
                              an in-progress patient record can't be lost. */}
                          <button
                            onClick={() =>
                              !isDeleteLocked(apt.originalStatus, apt.encounterId) &&
                              handleDelete(apt)
                            }
                            disabled={isDeleteLocked(apt.originalStatus, apt.encounterId)}
                            className={`flex h-[calc(36px*var(--app-scale))] w-[calc(36px*var(--app-scale))] items-center justify-center rounded-lg border ${
                              isDeleteLocked(apt.originalStatus, apt.encounterId)
                                ? 'cursor-not-allowed border-[#eaebf3] bg-gray-100'
                                : 'border-[#eaebf3] bg-[#eef6ff] hover:bg-[#dbeafe]'
                            }`}
                            title={
                              isDeleteLocked(apt.originalStatus, apt.encounterId)
                                ? 'Consultation is on hold — cannot delete until it is completed'
                                : 'Delete Appointment'
                            }
                          >
                            <Trash2
                              className={`h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] ${
                                isDeleteLocked(apt.originalStatus, apt.encounterId)
                                  ? 'text-gray-400'
                                  : 'text-[#6b8af9]'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pagination - Fixed at bottom right */}
          {!isLoading && totalItems > 0 && (
            <div className="fixed bottom-4 right-6">
              <div className="flex items-center gap-4 rounded-full bg-[#e6e8ef] px-4 py-2 shadow-md">
                <button
                  onClick={handlePrevPage}
                  disabled={!hasPrevPage}
                  className="flex h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] items-center justify-center disabled:opacity-50"
                >
                  <ChevronLeft className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#42434b]" />
                </button>
                <span className="font-hanken text-[calc(16px*var(--app-scale))] font-medium text-[#42434b]">
                  {currentPage} out of {totalPages || 1}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                  className="flex h-[calc(24px*var(--app-scale))] w-[calc(24px*var(--app-scale))] items-center justify-center disabled:opacity-50"
                >
                  <ChevronRight className="h-[calc(20px*var(--app-scale))] w-[calc(20px*var(--app-scale))] text-[#42434b]" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Appointment Modal - Using shared component */}
      <EditAppointmentModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedAppointment(null);
        }}
        appointment={selectedAppointment}
        onAppointmentUpdated={handleAppointmentUpdated}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onOpenChange={(open) => !isDeleting && setDeleteModalOpen(open)}
        title="Delete Appointment"
        description="Are you sure you want to delete this appointment? This action cannot be undone."
        footer={
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setDeleteModalOpen(false)}
              disabled={isDeleting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isDeleting}
              loading={isDeleting}
              className="flex-1 bg-red-600 hover:bg-red-700"
              onClick={handleDeleteConfirm}
            >
              {isDeleting ? 'Deleting...' : 'Delete Appointment'}
            </Button>
          </div>
        }
      >
        {selectedAppointment && (
          <div className="rounded-lg bg-gray-50 p-4">
            <p className="font-hanken text-sm text-gray-700">
              <span className="font-medium">Patient:</span> {selectedAppointment.patientName}
            </p>
            <p className="font-hanken text-sm text-gray-700">
              <span className="font-medium">Status:</span> {selectedAppointment.status}
            </p>
          </div>
        )}
      </Modal>

      {/* Patient Search Modal for Creating Appointment */}
      <PatientSearchModal
        isOpen={patientSearchModalOpen}
        onClose={() => setPatientSearchModalOpen(false)}
        onPatientSelected={handlePatientSelected}
        title="Create Appointment"
        description="Search for a patient by phone number to schedule an appointment"
        onSwitchToCreatePatient={handleSwitchToCreatePatient}
        onStartConsultation={canConsult ? handleStartConsultationFromSearch : undefined}
        isStartingConsultation={isStartingConsultationFromSearch}
      />

      {/* Patient Form Modal - Add Patient */}
      {addPatientFromQuery === 'true' && (
        <PatientFormModal isOpen={true} onClose={handleCloseFormModal}>
          <PatientFormContent
            onSuccess={handleFormSuccess}
            onCancel={handleCloseFormModal}
            isModal={true}
            onSwitchToAppointment={handleSwitchToAppointment}
            prefillPhone={prefillPhoneFromQuery || undefined}
          />
        </PatientFormModal>
      )}

      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={!!scheduleModalPatient}
        onClose={() => setScheduleModalPatient(null)}
        patient={scheduleModalPatient}
        onAppointmentCreated={handleAppointmentCreated}
      />
    </AppLayout>
  );
}

export default AppointmentManagementPage;
